import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  getProducts,
  getSuppliers,
  getPurchaseInvoices,
  savePurchaseInvoice,
  deletePurchaseInvoice,
  getNextPurchaseInvoiceNumber,
  Product,
  Supplier,
  PurchaseInvoiceDocumentRecord,
} from '@/services/firestore'
import { friendlyError } from '@/utils/friendlyError'
import { FilePlus, ShoppingCart, Trash2, RotateCcw, Save, Search, Scan, History, Printer, X, FileText } from 'lucide-react'
import { PurchaseInvoiceDocument } from '@/components/invoice/PurchaseInvoiceDocument'
import { useReactToPrint } from 'react-to-print'

export const Route = createFileRoute('/admin/purchase-invoices')({
  component: PurchaseInvoicesPage,
})

interface EntryItem {
  id: string; // unique ID for list
  productId: string;
  name: string;
  code: string;
  hsCode?: string;
  brand?: string;
  price: number;
  quantity: number;
  uom: string;
  taxRate: number;
}

function PurchaseInvoicesPage() {
  const { user } = useAuth()
  
  // Data
  const [products, setProducts] = useState<Product[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [invoices, setInvoices] = useState<PurchaseInvoiceDocumentRecord[]>([])
  const [loadingInitial, setLoadingInitial] = useState(true)

  // Form State
  const [cart, setCart] = useState<EntryItem[]>([])
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)
  const [supplierSearch, setSupplierSearch] = useState('')
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false)
  
  const [productSearch, setProductSearch] = useState('')
  const [showProductDropdown, setShowProductDropdown] = useState(false)

  // Meta
  const [date, setDate] = useState(new Date().toISOString().slice(0, 16))
  const [docNumber, setDocNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [discount, setDiscount] = useState(0)

  // Progress UI
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false)

  // History Modal & Printing
  const [showHistory, setShowHistory] = useState(false)
  const [historySearch, setHistorySearch] = useState('')
  const [printData, setPrintData] = useState<PurchaseInvoiceDocumentRecord | null>(null)

  const printRef = useRef<HTMLDivElement>(null)
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `PurchaseInvoice_${printData?.purchaseInvoiceNumber || 'Doc'}`,
    onAfterPrint: () => setPrintData(null),
  })

  useEffect(() => {
    if (printData && printRef.current) {
      handlePrint()
    }
  }, [printData, handlePrint])

  useEffect(() => {
    Promise.all([getProducts(), getSuppliers(), getPurchaseInvoices()])
      .then(([p, s, inv]) => {
        setProducts(p)
        setSuppliers(s as Supplier[])
        setInvoices(inv as PurchaseInvoiceDocumentRecord[])
      })
      .catch(err => console.error('Failed to load initial data:', err))
      .finally(() => setLoadingInitial(false))
  }, [])

  const reloadInvoices = async () => {
    try {
      const inv = await getPurchaseInvoices()
      setInvoices(inv as PurchaseInvoiceDocumentRecord[])
    } catch (e) {
      console.error(e)
    }
  }

  // Load Previous Entry
  const loadPreviousEntry = async () => {
    setIsLoadingPrevious(true)
    try {
      const pastInvoices = await getPurchaseInvoices()
      if (!pastInvoices || pastInvoices.length === 0) {
        alert('No previous purchase records found.')
        return
      }

      // First record is the most recent
      const lastInv = pastInvoices[0] as PurchaseInvoiceDocumentRecord

      // Populate Cart
      const mappedItems: EntryItem[] = (lastInv.items || []).map((it: any) => ({
        id: crypto.randomUUID(),
        productId: it.productId || 'custom',
        name: it.name || it.description || '',
        code: it.code || it.itemCode || '',
        hsCode: it.hsCode,
        brand: it.brand,
        price: Number(it.price || it.unitRate || 0),
        quantity: Number(it.quantity || 1),
        uom: it.uom || 'PCS',
        taxRate: Number(it.taxRate || 0),
      }))

      setCart(mappedItems)

      // Populate Supplier
      if (lastInv.supplierId) {
        const s = suppliers.find(x => x.id === lastInv.supplierId)
        if (s) {
          setSelectedSupplier(s)
          setSupplierSearch('')
        } else {
          setSelectedSupplier(null)
          setSupplierSearch(lastInv.supplierName || lastInv.companyName || '')
        }
      } else if (lastInv.supplierName || lastInv.companyName) {
        setSelectedSupplier(null)
        setSupplierSearch(lastInv.supplierName || lastInv.companyName || '')
      } else {
        setSelectedSupplier(null)
        setSupplierSearch('')
      }

      setDiscount(lastInv.discountAmount || 0)
      setNotes(lastInv.notes || '')
    } catch (err) {
      alert(friendlyError(err, 'Could not fetch previous record'))
    } finally {
      setIsLoadingPrevious(false)
    }
  }

  // Cart operations
  const addProductToCart = (prod: Product) => {
    setCart(prev => {
      const existing = prev.find(i => i.productId === prod.id)
      if (existing) {
        return prev.map(i => i.productId === prod.id ? { ...i, quantity: i.quantity + 1 } : i)
      }
      const initialRate = (prod.costPrice && prod.costPrice > 0) ? prod.costPrice : (prod.price || 0)
      return [...prev, {
        id: crypto.randomUUID(),
        productId: prod.id!,
        name: prod.name,
        code: prod.code,
        hsCode: prod.hsCode,
        brand: prod.brand,
        price: initialRate,
        quantity: 1,
        uom: prod.unit || 'PCS',
        taxRate: prod.taxRate || 0,
      }]
    })
    setProductSearch('')
    setShowProductDropdown(false)
  }

  const updateCartItem = (id: string, field: keyof EntryItem, value: any) => {
    setCart(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item))
  }

  const removeCartItem = (id: string) => {
    setCart(prev => prev.filter(i => i.id !== id))
  }

  // Calculations
  const subtotal = cart.reduce((acc, i) => acc + (i.price * i.quantity), 0)
  const taxAmount = cart.reduce((acc, i) => acc + Math.round((i.price * i.quantity) * (i.taxRate / 100)), 0)
  const totalAmount = subtotal + taxAmount - discount

  // Submit
  const handleSubmit = async () => {
    if (cart.length === 0) {
      alert('Cannot submit an empty purchase record.')
      return
    }

    setIsSubmitting(true)
    try {
      const finalInvoiceNumber = await getNextPurchaseInvoiceNumber()

      // Convert local datetime to Date object
      const targetDate = new Date(date)
      const dateStr = date.split('T')[0]

      const supplierDisplayName = selectedSupplier?.name || supplierSearch.trim() || 'General Supplier'
      
      const newInvoice: PurchaseInvoiceDocumentRecord = {
        purchaseInvoiceNumber: docNumber.trim() ? docNumber.trim() : finalInvoiceNumber,
        docNumber: docNumber.trim() || undefined,
        date: dateStr,
        invoiceDate: dateStr,
        supplierId: selectedSupplier?.id || undefined,
        supplierName: supplierDisplayName,
        companyName: supplierDisplayName,
        companyAddress: selectedSupplier?.address || selectedSupplier?.city || '',
        items: cart.map((i, idx) => ({
          sNo: idx + 1,
          productId: i.productId,
          name: i.name,
          description: i.name,
          code: i.code,
          itemCode: i.code,
          hsCode: i.hsCode,
          brand: i.brand,
          uom: i.uom,
          unitRate: i.price,
          price: i.price,
          quantity: i.quantity,
          taxRate: i.taxRate,
          taxAmount: Math.round(i.price * i.quantity * (i.taxRate / 100)),
          total: (i.price * i.quantity).toString(),
        })),
        subtotal,
        taxRate: 0,
        taxAmount,
        discountAmount: discount,
        totalAmount,
        notes,
        status: 'completed',
        createdBy: user?.uid || '',
        createdAt: targetDate,
      }

      const savedId = await savePurchaseInvoice(newInvoice)

      const askPrint = window.confirm('Purchase entry recorded successfully!\n\nDo you want to print the purchase invoice now?')
      if (askPrint) {
        setPrintData({ ...newInvoice, id: savedId })
      }
      
      // Clear form
      setCart([])
      setSelectedSupplier(null)
      setSupplierSearch('')
      setDocNumber('')
      setNotes('')
      setDiscount(0)
      setDate(new Date().toISOString().slice(0, 16))

      reloadInvoices()

    } catch (err) {
      alert(friendlyError(err, 'Failed to save purchase record'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteInvoice = async (id: string | undefined) => {
    if (!id || !confirm('Delete this purchase invoice permanently?')) return
    try {
      await deletePurchaseInvoice(id)
      reloadInvoices()
    } catch (err) {
      alert(friendlyError(err, 'Could not delete invoice'))
    }
  }

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(productSearch.toLowerCase()) || 
    p.code.toLowerCase().includes(productSearch.toLowerCase())
  ).slice(0, 10)

  const filteredSuppliers = suppliers.filter(s => 
    s.name.toLowerCase().includes(supplierSearch.toLowerCase()) || 
    (s.phone && s.phone.includes(supplierSearch)) ||
    (s.city && s.city.toLowerCase().includes(supplierSearch.toLowerCase()))
  ).slice(0, 5)

  const filteredHistory = invoices.filter(inv => 
    !historySearch ||
    (inv.purchaseInvoiceNumber && inv.purchaseInvoiceNumber.toLowerCase().includes(historySearch.toLowerCase())) ||
    (inv.supplierName && inv.supplierName.toLowerCase().includes(historySearch.toLowerCase())) ||
    (inv.companyName && inv.companyName.toLowerCase().includes(historySearch.toLowerCase())) ||
    (inv.docNumber && inv.docNumber.toLowerCase().includes(historySearch.toLowerCase()))
  )

  if (loadingInitial) {
    return <div className="p-8 text-center text-slate-500 font-medium">Loading modules...</div>
  }

  return (
    <div className="p-4 lg:p-8 bg-[#f0f5ff] min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-800 flex items-center gap-3">
            <FilePlus className="w-7 h-7 text-blue-600" />
            Manual Purchase Entry
          </h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">Record purchases from suppliers directly into the system database.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowHistory(true)}
            className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all flex items-center gap-2 hover:shadow-md hover:border-blue-300"
          >
            <History className="w-4 h-4 text-blue-600" />
            <span>Purchase History</span>
            <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-black">{invoices.length}</span>
          </button>

          <button 
            onClick={loadPreviousEntry}
            disabled={isLoadingPrevious}
            className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all flex items-center gap-2 hover:shadow-md hover:border-blue-300 disabled:opacity-50"
          >
            {isLoadingPrevious ? <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"/> : <RotateCcw className="w-4 h-4" />}
            Load Previous Record
          </button>
        </div>
      </div>

      {/* Main Grid: Exact 3 columns like sales-entry */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* LEFT COLUMN: Data Input */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          
          {/* Metadata Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h2 className="text-[13px] font-bold tracking-widest text-slate-400 uppercase mb-4">Invoice Information</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1.5">Date & Time</label>
                <input 
                  type="datetime-local" 
                  value={date} 
                  onChange={e => setDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-slate-800" 
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1.5">Document Number (Optional)</label>
                <input 
                  type="text" 
                  value={docNumber} 
                  onChange={e => setDocNumber(e.target.value)}
                  placeholder="e.g. PINV-9981 or Bill #"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-slate-800" 
                />
              </div>

              {/* SUPPLIER: Exact match to customer in sales-entry */}
              <div className="sm:col-span-2 relative">
                <label className="text-xs font-bold text-slate-600 block mb-1.5">Supplier</label>
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input 
                    type="text" 
                    value={selectedSupplier ? selectedSupplier.name : supplierSearch} 
                    onChange={e => {
                      if (selectedSupplier) setSelectedSupplier(null)
                      setSupplierSearch(e.target.value)
                      setShowSupplierDropdown(true)
                    }}
                    onFocus={() => setShowSupplierDropdown(true)}
                    placeholder="Search supplier or leave blank for General Supplier"
                    className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-slate-800 bg-slate-50/50" 
                  />
                  {selectedSupplier && (
                    <button onClick={() => { setSelectedSupplier(null); setSupplierSearch(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {showSupplierDropdown && (
                  <div className="fixed inset-0 z-10" onClick={() => setShowSupplierDropdown(false)} />
                )}

                {showSupplierDropdown && supplierSearch && !selectedSupplier && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto">
                    {filteredSuppliers.length > 0 ? filteredSuppliers.map(s => (
                      <button 
                        key={s.id} 
                        onClick={() => { setSelectedSupplier(s); setShowSupplierDropdown(false); setSupplierSearch(''); }}
                        className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                      >
                        <div className="font-bold text-slate-800 text-sm">{s.name}</div>
                        {(s.phone || s.city) && (
                          <div className="text-[10px] text-slate-500 flex gap-2">
                            {s.phone && <span>{s.phone}</span>}
                            {s.city && <span>• {s.city}</span>}
                          </div>
                        )}
                      </button>
                    )) : (
                      <div className="px-4 py-3 text-sm text-slate-500">No matching suppliers found. Will record as: "{supplierSearch}"</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Items Section: Exact match to sales-entry */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col flex-1 overflow-hidden min-h-[400px]">
            <div className="p-5 border-b border-slate-100">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-[13px] font-bold tracking-widest text-slate-400 uppercase">Items</h2>
              </div>

              <div className="relative z-10">
                <div className="relative">
                  <Scan className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value)
                      setShowProductDropdown(true)
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    placeholder="Search product code or name..."
                    className="w-full pl-9 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all placeholder:font-normal"
                  />
                </div>

                {showProductDropdown && (
                  <div className="fixed inset-0 z-[5]" onClick={() => setShowProductDropdown(false)} />
                )}

                {showProductDropdown && productSearch && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden max-h-60 overflow-y-auto">
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map(p => {
                        const itemPrice = (p.costPrice && p.costPrice > 0) ? p.costPrice : p.price
                        return (
                          <button
                            key={p.id}
                            onClick={() => addProductToCart(p)}
                            className="w-full flex items-center justify-between p-3 hover:bg-blue-50 border-b border-slate-50 transition-colors last:border-0"
                          >
                            <div className="text-left">
                              <div className="font-bold text-slate-800 text-sm">{p.name}</div>
                              <div className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded inline-block mt-1 font-mono">{p.code}</div>
                            </div>
                            <div className="font-black text-blue-600 text-sm">Rs {itemPrice.toLocaleString()}</div>
                          </button>
                        )
                      })
                    ) : (
                      <div className="p-4 text-center text-slate-500 text-sm font-medium">No products found</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-auto bg-slate-50/50 p-5">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 py-12">
                  <ShoppingCart className="w-12 h-12 mb-3 stroke-1 text-slate-300" />
                  <p className="font-medium text-sm">No items added to the record.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map((item, idx) => (
                    <div key={item.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center gap-4 group">
                      <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <input
                          value={item.name}
                          onChange={(e) => updateCartItem(item.id, 'name', e.target.value)}
                          className="font-bold text-slate-800 text-sm bg-transparent border-none p-0 focus:ring-0 w-full mb-1 truncate"
                        />
                        <div className="flex text-xs text-slate-500 gap-3">
                          <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{item.code}</span>
                          <span>UOM: <input className="w-12 bg-transparent text-slate-700 font-bold border-b border-slate-200 focus:border-blue-500 outline-none" value={item.uom} onChange={e => updateCartItem(item.id, 'uom', e.target.value)}/></span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 shrink-0 bg-slate-50 p-2 rounded-lg border border-slate-100">
                        <div className="flex flex-col items-center">
                          <label className="text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Price</label>
                          <input type="number" value={item.price} onChange={e => updateCartItem(item.id, 'price', Number(e.target.value))} className="w-20 text-center font-bold text-sm bg-white border border-slate-200 rounded px-1 py-1 focus:outline-none focus:border-blue-500" />
                        </div>
                        <div className="text-slate-300 text-lg font-light">×</div>
                        <div className="flex flex-col items-center">
                          <label className="text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Qty</label>
                          <input type="number" value={item.quantity} onChange={e => updateCartItem(item.id, 'quantity', Number(e.target.value))} className="w-16 text-center font-bold text-sm bg-white border border-slate-200 rounded px-1 py-1 focus:outline-none focus:border-blue-500" />
                        </div>
                        <div className="text-slate-300 text-lg font-light">=</div>
                        <div className="flex flex-col items-end w-24">
                          <label className="text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Total</label>
                          <div className="font-black text-blue-600">{(item.price * item.quantity).toLocaleString()}</div>
                        </div>
                      </div>

                      <button onClick={() => removeCartItem(item.id)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Summary & Action */}
        <div className="flex flex-col gap-6">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-xl text-white overflow-hidden">
            <div className="p-6">
              <h2 className="text-[13px] font-bold tracking-widest text-slate-400 uppercase mb-6 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" /> Summary
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center text-slate-300 text-sm">
                  <span>Subtotal</span>
                  <span className="font-mono">Rs {subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-slate-300 text-sm">
                  <span>Sales / Purchase Tax</span>
                  <span className="font-mono">Rs {taxAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-slate-300 text-sm">
                  <span>Discount</span>
                  <div className="flex items-center gap-1">
                    <span className="text-red-400">- Rs</span>
                    <input 
                      type="number" 
                      value={discount || ''} 
                      onChange={e => setDiscount(Number(e.target.value))}
                      className="bg-slate-800/50 border border-slate-600 rounded px-2 py-1 w-20 text-right font-mono focus:outline-none focus:border-blue-400 text-white"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-black/20 p-6 pt-5">
              <div className="flex justify-between items-end mb-6">
                <span className="text-slate-400 font-bold uppercase tracking-wider text-xs">Total Amount</span>
                <span className="text-3xl font-black text-white tracking-tight">Rs {totalAmount.toLocaleString()}</span>
              </div>

              <div className="mb-6">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">Remarks / Notes</label>
                <textarea 
                  value={notes} 
                  onChange={e => setNotes(e.target.value)}
                  className="w-full bg-slate-800/50 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500 placeholder-slate-500 font-medium min-h-[80px]"
                  placeholder="Additional context for this purchase entry..."
                />
              </div>

              <button 
                onClick={handleSubmit}
                disabled={isSubmitting || cart.length === 0}
                className="w-full bg-blue-500 hover:bg-blue-400 text-white py-4 rounded-xl font-black text-[15px] shadow-lg shadow-blue-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:shadow-none hover:-translate-y-0.5 active:translate-y-0"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving Record...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Record Purchase Entry
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Print Area */}
      <div style={{ display: 'none' }}>
        {printData && <PurchaseInvoiceDocument ref={printRef} data={printData} />}
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <h3 className="text-base font-bold text-slate-800">Purchase Invoices History</h3>
                <span className="text-xs bg-blue-100 text-blue-700 font-extrabold px-2 py-0.5 rounded-full">{invoices.length}</span>
              </div>
              <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:bg-slate-200 p-1.5 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b border-slate-100 bg-slate-50/30">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input 
                  type="text" 
                  value={historySearch} 
                  onChange={e => setHistorySearch(e.target.value)} 
                  placeholder="Search by invoice #, supplier, or document #..." 
                  className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-auto p-4">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-wider">
                    <th className="text-left px-3 py-2.5 font-bold">Invoice #</th>
                    <th className="text-left px-3 py-2.5 font-bold">Date</th>
                    <th className="text-left px-3 py-2.5 font-bold">Supplier</th>
                    <th className="text-center px-3 py-2.5 font-bold">Items</th>
                    <th className="text-right px-3 py-2.5 font-bold">Total</th>
                    <th className="text-center px-3 py-2.5 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((inv, idx) => {
                    const invTotal = inv.totalAmount || (inv.items || []).reduce((acc, it) => acc + (Number(it.total) || (Number(it.unitRate || it.price || 0) * Number(it.quantity || 1))), 0)
                    return (
                      <tr key={inv.id || idx} className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors">
                        <td className="px-3 py-2.5 font-mono font-bold text-blue-600">{inv.purchaseInvoiceNumber}</td>
                        <td className="px-3 py-2.5 text-slate-600 font-medium">{inv.date || inv.invoiceDate}</td>
                        <td className="px-3 py-2.5 font-bold text-slate-800">{inv.supplierName || inv.companyName}</td>
                        <td className="px-3 py-2.5 text-center text-slate-500 font-bold">{inv.items?.length || 0}</td>
                        <td className="px-3 py-2.5 text-right font-black text-slate-900 font-mono">Rs {invTotal.toLocaleString()}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-center gap-1.5">
                            <button onClick={() => setPrintData(inv)} className="text-slate-400 hover:text-green-600 hover:bg-green-50 p-1.5 rounded-lg transition-colors" title="Print Invoice">
                              <Printer className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteInvoice(inv.id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-colors" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                  {filteredHistory.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-10 text-slate-400 font-medium">No purchase invoices found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
